import { Input } from "antd-mobile";
import {
    Fragment,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { FC, ReactNode } from "react";
import {
    IconCirclePlus,
    IconDownFill,
    IconLoading,
    IconSelect,
    IconSwapLoading,
} from "~/components";
import { PairedToken, TradeToken } from "~/types";
import { useTranslation } from "react-i18next";
import { createOrder, fetchApproveCheck, submitOrder } from "~/api/endpoints";
import { formatValueFixed } from "~/utils";
import ModalUTokenList from "~/components/UTokenList";
import { useUXUYWallet } from "~/Provider/UXUYWallet";
import { CHAIN_IDS } from "@uxuycom/web3-tg-sdk";
import { useSnackbar } from "~/Provider/Snackbar";
import { ActionType, ORDER_PLATFORM } from "../../constants";
import { debounce } from "~/tools/common";
import useFetchTradePrice from "../hooks/useFetchTradePrice";
import useIntervalUpdatePrice from "../hooks/useIntervalUpdatePrice";
import TradeActionSegmentedButton from "../components/TradeActionSegmentedButton";
import {
    openTelegramLink,
    themeParams,
    useSignal,
} from "@telegram-apps/sdk-react";
import { caculateSlippageValue, getWalletReceiveLink } from "../utils";
import TokenAmountSelector from "../components/TokenAmountSelector";
import eventBus from "~/tools/eventBus";
import { formatBalance } from "~/utils/number-format";
import BigNumber from "bignumber.js";
import { xyLogEvent } from "~/hook/useFirebase";

interface IProps {
    children?: ReactNode;
    loading: boolean;
    tradeActionType: ActionType;
    setTradeActiveType: (
        tradeToken: TradeToken,
        tradeActionType: ActionType
    ) => void;
    tradeToken: TradeToken;
    pairedToken: PairedToken;
    setPairedToken: (tradeToken: TradeToken, pairedToken: PairedToken) => void;
    slippageValue: string | undefined;
    setTradeOrderId: (order_id?: string | undefined) => void;
}

// 定义 ModalUTokenList 的类型
interface ModalUTokenListRef {
    open: () => void; // 确保 open 方法存在
}

const TradeBodySwap: FC<IProps> = ({
    loading,
    tradeActionType,
    setTradeActiveType,
    tradeToken,
    pairedToken,
    setPairedToken,
    slippageValue,
    setTradeOrderId,
}) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const {
        getAddress,
        getAddressType,
        sendTransaction,
        connectTo,
        isConnectedTo,
        isConnecting,
        waitForTransaction,
    } = useUXUYWallet();
    const isDark = useSignal(themeParams.isDark);

    const [isTopAmount, setIsTopAmount] = useState(false);
    const [topAmount, setTopAmount] = useState("");
    const [bottomAmount, setBottomAmount] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const refModalTokenList = useRef<ModalUTokenListRef>(null); // 使用自定义类型

    const [visible, setVisible] = useState(false);
    const options = [
        { id: String(ORDER_PLATFORM.market), name: t("market_price") },
        { id: String(ORDER_PLATFORM.oneClick), name: t("One_click_trading") },
        { id: String(ORDER_PLATFORM.Limit), name: t("limit_trading") },
    ];

    const tradeTokenChainId = String(tradeToken?.token_base?.chain_id);
    const address = getAddress(tradeTokenChainId);

    const availableBalance = useMemo(
        () =>
            tradeActionType === ActionType.Buy
                ? pairedToken?.balance
                : tradeToken?.balance,
        [tradeActionType, tradeToken, pairedToken]
    );
    const availableSymbol = useMemo(
        () =>
            (tradeActionType === ActionType.Buy
                ? pairedToken
                : tradeToken
            )?.token_base?.symbol?.toUpperCase(),
        [tradeActionType, tradeToken, pairedToken]
    );

    const isInsufficientBalance = useMemo(
        () => new BigNumber(availableBalance ?? "0").isZero(),
        [availableBalance]
    );

    const ignoreFetchPriceRef = useRef(false);

    const { isFetching, fetchPrice, fetchedRouter, setFetchedRouter } =
        useFetchTradePrice();

    const slippage = useMemo(
        () => caculateSlippageValue(slippageValue),
        [slippageValue]
    );

    const fetchTokenPrice = useCallback(async () => {
        if (topAmount === "" && bottomAmount === "") return;

        if (isTopAmount && topAmount !== "") {
            await fetchPrice(
                tradeActionType,
                tradeToken,
                pairedToken,
                isTopAmount,
                topAmount,
                "",
                slippage
            );
            return;
        }

        if (!isTopAmount && bottomAmount !== "") {
            await fetchPrice(
                tradeActionType,
                tradeToken,
                pairedToken,
                isTopAmount,
                "",
                bottomAmount,
                slippage
            );
            return;
        }
    }, [
        isTopAmount,
        topAmount,
        bottomAmount,
        tradeActionType,
        tradeToken,
        pairedToken,
        slippage,
    ]);

    useIntervalUpdatePrice(fetchTokenPrice);

    // 通用处理输入变化函数
    const handleInputChange = debounce(
        async (
            tradeActionType: ActionType,
            tradeToken: TradeToken,
            pairedToken: PairedToken,
            isTopAmount: boolean,
            topAmount: string,
            bottomAmount: string,
            slippage: number
        ) => {
            const result = await fetchPrice(
                tradeActionType,
                tradeToken,
                pairedToken,
                isTopAmount,
                topAmount,
                bottomAmount,
                slippage
            );

            if (
                result &&
                result.routers &&
                result.routers.length > 0 &&
                !ignoreFetchPriceRef.current
            ) {
                const router = result.routers[0];
                if (isTopAmount) {
                    if (topAmount !== "") {
                        setBottomAmount(formatValueFixed(router.amount)); // 更新 bottomAmount
                    }
                } else {
                    if (bottomAmount !== "") {
                        setTopAmount(formatValueFixed(router.amount)); // 更新 topAmount
                    }
                }
            }
        }
    );

    // 处理 topAmount 输入变化
    const handleTopAmountChange = (value: string, trigger?: boolean) => {
        if (trigger !== true) {
            eventBus.emit("TradeFormTopInputChange", value);
        }

        setIsTopAmount(true);
        setTopAmount(value);

        if (value === "") {
            ignoreFetchPriceRef.current = true;
            setBottomAmount("");
            setFetchedRouter(null);
            return;
        }

        if (/^\d*\.$/.test(value)) return;

        ignoreFetchPriceRef.current = false;
        handleInputChange(
            tradeActionType,
            tradeToken,
            pairedToken,
            true,
            value,
            "",
            slippage
        );
    };

    // 处理 bottomAmount 输入变化
    const handleBottomAmountChange = (value: string, trigger?: boolean) => {
        if (trigger !== true) {
            eventBus.emit("TradeFormBottomInputChange", value);
        }

        setIsTopAmount(false);
        setBottomAmount(value);

        if (value === "") {
            ignoreFetchPriceRef.current = true;
            setTopAmount("");
            setFetchedRouter(null);
            return;
        }

        if (/^\d*\.$/.test(value)) return;

        ignoreFetchPriceRef.current = false;
        handleInputChange(
            tradeActionType,
            tradeToken,
            pairedToken,
            false,
            "",
            value,
            slippage
        );
    };

    useEffect(() => {
        setTopAmount("");
        setBottomAmount("");
        setFetchedRouter(null);
        setIsLoading(false);
        ignoreFetchPriceRef.current = true;
    }, [
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract,
        pairedToken?.token_base?.chain_id,
        pairedToken?.token_base?.contract,
        tradeActionType,
    ]);

    const handleClick = async () => {
        if (!isConnectedTo(tradeTokenChainId)) {
            await connectTo(tradeTokenChainId);
            return;
        }

        if (isInsufficientBalance) {
            handleRevicePayment();
            return;
        }

        if (!fetchedRouter || !topAmount || !bottomAmount) return;

        setIsLoading(true);
        const fromAddress = String(address);
        const toAddress = fromAddress;
        const fromChainId =
            tradeActionType === ActionType.Buy
                ? pairedToken?.token_base?.chain_id
                : tradeToken?.token_base?.chain_id;
        const fromContract =
            tradeActionType === ActionType.Buy
                ? pairedToken?.token_base?.contract
                : tradeToken?.token_base?.contract;
        const toChainId =
            tradeActionType === ActionType.Buy
                ? tradeToken?.token_base?.chain_id
                : pairedToken?.token_base?.chain_id;
        const toContract =
            tradeActionType === ActionType.Buy
                ? tradeToken?.token_base?.contract
                : pairedToken?.token_base?.contract;

        xyLogEvent("Trade:Start", {
            type: tradeActionType === ActionType.Buy ? "buy" : "sell",
            from: { chain_id: fromChainId, contract: fromContract },
            to: { chain_id: toChainId, contract: toContract },
        });

        try {
            const result = await fetchApproveCheck(
                String(fromChainId),
                fromAddress,
                getAddressType(String(fromChainId)) as number,
                String(fromContract)
            );
            const isApproved = result.data.approved;

            if (!isApproved) {
                xyLogEvent("Trade:ApproveStart", {
                    type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                    from: { chain_id: fromChainId, contract: fromContract },
                    to: { chain_id: toChainId, contract: toContract },
                });
                const approveData = result.data;
                const r = await sendTransaction(
                    String(fromChainId),
                    approveData
                );
                await waitForTransaction(String(fromChainId), r);
                xyLogEvent("Trade:ApproveFinfished", {
                    type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                    from: { chain_id: fromChainId, contract: fromContract },
                    to: { chain_id: toChainId, contract: toContract },
                });
            }

            let params: any = {};

            const isAutomaticSlippage =
                slippageValue == null || slippageValue.length === 0;
            const slippageParam = isAutomaticSlippage
                ? Math.max(10000, Number(fetchedRouter.slippage))
                : slippage;
            if (tradeActionType === ActionType.Buy) {
                params = {
                    from_address: fromAddress,
                    from_chain_id: Number(fromChainId),
                    from_contract: fromContract,
                    from_amount: isTopAmount
                        ? fetchedRouter.amount
                        : bottomAmount,

                    to_address: toAddress,
                    to_chain_id: Number(toChainId),
                    to_contract: toContract,
                    to_amount: isTopAmount ? topAmount : fetchedRouter.amount,

                    slippage: slippageParam,
                    is_auto_slippage: isAutomaticSlippage,
                    order_type: 1,
                    direction: 1,
                };
            } else {
                params = {
                    from_address: fromAddress,
                    from_chain_id: Number(fromChainId),
                    from_contract: fromContract,
                    from_amount: isTopAmount ? topAmount : fetchedRouter.amount,

                    to_address: toAddress,
                    to_chain_id: Number(toChainId),
                    to_contract: toContract,
                    to_amount: isTopAmount
                        ? fetchedRouter.amount
                        : bottomAmount,

                    slippage: slippageParam,
                    is_auto_slippage: isAutomaticSlippage,
                    order_type: 1,
                    direction: 2,
                };
            }

            const orderInfo = await createOrder(params as any);
            const tradeOrder = orderInfo.data;
            xyLogEvent("Trade:CreateOrderSuccess", {
                type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                from: { chain_id: fromChainId, contract: fromContract },
                to: { chain_id: toChainId, contract: toContract },
                order: { id: tradeOrder?.order_id },
            });

            const chainId = String(tradeOrder.swap_info.from.chain_id);

            xyLogEvent("Trade:SignStart", {
                type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                from: { chain_id: fromChainId, contract: fromContract },
                to: { chain_id: toChainId, contract: toContract },
            });
            const transactionCode = await sendTransaction(chainId, tradeOrder);
            xyLogEvent("Trade:SignSuccessed", {
                type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                from: { chain_id: fromChainId, contract: fromContract },
                to: { chain_id: toChainId, contract: toContract },
            });
            const isSolana = chainId === String(CHAIN_IDS.SOLANA);
            if (isSolana) {
                await submitOrder(
                    tradeOrder.order_id,
                    undefined,
                    transactionCode
                );
            } else {
                await submitOrder(
                    tradeOrder.order_id,
                    transactionCode,
                    undefined
                );
            }

            xyLogEvent("Trade:Success", {
                type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                from: { chain_id: fromChainId, contract: fromContract },
                to: { chain_id: toChainId, contract: toContract },
            });

            setTradeOrderId(tradeOrder.order_id);
            setTopAmount("");
            setBottomAmount("");
            setFetchedRouter(null);
            setIsLoading(false);
            ignoreFetchPriceRef.current = true;
        } catch (err: any) {
            xyLogEvent("Trade:Interrupted", {
                type: tradeActionType === ActionType.Buy ? "buy" : "sell",
                from: { chain_id: fromChainId, contract: fromContract },
                to: { chain_id: toChainId, contract: toContract },
                err: err,
            });
            if ("code" in err) {
                snackbar.error(err.message);
                return;
            }
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const [selectedOption] = useState<{
        id: string;
        name: string;
    }>(options[0]);

    const renderDropdownOption = (
        option: { id: string; name: string },
        isLast: boolean
    ) => (
        <div
            key={option.id} // 使用id作为key属性
            onClick={() => handleOptionClick(option)}
            className={`option cursor-pointer flex justify-between ${selectedOption.id === option.id ? "text-text_1" : "text-text_3"} items-center text-text_1 pr-2 ml-2 py-2 bg-bg_0  my-[2px] ${!isLast ? "border-b-[0.5px] border-border_2" : ""}  text-body3 `} // 根据是否为最后一个选项添加边框
        >
            {option.name}
            {selectedOption.id === option.id && (
                <IconSelect className="text-[16px] text-brand_1" />
            )}
            {/* 仅当选中时显示图标 */}
        </div>
    );

    const handleOptionClick = (option: { id: string; name: string }) => {
        // setSelectedOption(option); // 设置选中的选项
        console.log(option.name);
        setVisible(false); // 关闭弹框
        snackbar.info(t("comming_soon"));
    };

    const handleRevicePayment = () => {
        const token =
            tradeActionType === ActionType.Buy ? pairedToken : tradeToken;

        if (!token || !token.token_base) return;
        if (!isConnectedTo(String(token.token_base.chain_id))) return;

        const receiveLink = getWalletReceiveLink(
            String(token.token_base.chain_id),
            token.token_base.contract
        );
        openTelegramLink(receiveLink);
    };

    return (
        <div className={`flex flex-1 flex-col max-w-full`}>
            <TradeActionSegmentedButton
                tradeToken={tradeToken}
                tradeActionType={tradeActionType}
                setTradeActiveType={setTradeActiveType}
            />
            <div className="relative">
                <div className="bg-bg_bottom rounded-[4px] flex items-center justify-between px-[8px] py-[4px]">
                    <div
                        className="flex justify-between items-center w-full cursor-pointer "
                        onClick={() => setVisible(!visible)}
                    >
                        {/* 左对齐的文本 */}
                        <div className="flex-1 border-none bg-transparent text-text_1 placeholder:text-text_3 text-left">
                            {t("market_price")}
                        </div>

                        {/* 右对齐的图标 */}
                        <div className="flex items-center justify-end">
                            <IconDownFill className="text-text_3 text-[14px]" />
                        </div>
                    </div>
                </div>
                {visible && (
                    <div
                        className="absolute right-0 w-[205px] bg-bg_0 rounded-[8px] p-[2px]  border-[0.5px] border-border_2 mt-1 top-full z-20"
                        style={{
                            boxShadow: `0px 8px 20px 0px rgba(0, 0, 0, ${isDark ? "0.4" : "0.1"})`,
                        }}
                    >
                        {options.map((option, index) =>
                            renderDropdownOption(
                                option,
                                index === options.length - 1
                            )
                        )}
                    </div>
                )}
            </div>
            <div className="my-[8px] bg-bg_bottom rounded-[4px] h-[44px] flex items-center justify-between px-[8px]">
                {isFetching &&
                    (topAmount === null || topAmount.length == 0) && (
                        <div className="loading-rotate-1s w-[22px] h-[22px]">
                            <IconSwapLoading className="loading-rotate-1s text-[24px] text-text_3" />
                        </div>
                    )}
                <Input
                    className="flex-1 min-w-0 border-none bg-transparent text-text_1 text-body3 placeholder:text-body3 placeholder:text-text_3 h-full font-semibold"
                    style={{
                        "--color": "var(--color_text_1)",
                        "--placeholder-color": "var(--color_text_3)",
                        "--font-size": "14px",
                    }}
                    placeholder={isFetching ? "" : t("amount")}
                    value={topAmount}
                    type="text"
                    inputMode="decimal"
                    onChange={(value) => {
                        if (/^\d*\.?\d*$/.test(value)) {
                            handleTopAmountChange(value);
                        }
                    }}
                />
                <label className="text-text_1 text-body3 ml-2 font-semibold">
                    {tradeToken?.token_base?.symbol?.toUpperCase()}
                </label>
            </div>
            <TokenAmountSelector
                tradeActionType={tradeActionType}
                tradeToken={tradeToken}
                pairedToken={pairedToken}
                availableSymbol={availableSymbol}
                handleTopAmountChange={handleTopAmountChange}
                handleBottomAmountChange={handleBottomAmountChange}
            />
            <div className="my-[8px] bg-bg_bottom rounded-[4px] h-[44px] flex items-center px-[8px]">
                {isFetching &&
                    (bottomAmount === null || bottomAmount.length == 0) && (
                        <div className="loading-rotate-1s w-[22px] h-[22px]">
                            <IconSwapLoading className="loading-rotate-1s text-[24px] text-text_3" />
                        </div>
                    )}
                <Input
                    style={{
                        "--color": "var(--color_text_1)",
                        "--placeholder-color": "var(--color_text_3)",
                        "--font-size": "14px",
                    }}
                    className="flex-1 min-w-0 border-none bg-transparent text-text_1 text-body3 placeholder:text-body3 placeholder:text-text_3 h-full font-semibold"
                    placeholder={isFetching ? "" : t("total")}
                    value={bottomAmount}
                    type="text"
                    inputMode="decimal"
                    onChange={(value) => {
                        if (/^\d*\.?\d*$/.test(value)) {
                            handleBottomAmountChange(value);
                        }
                    }}
                />
                <Fragment>
                    <div
                        className="flex items-center h-[28px] ml-[8px] cursor-pointer bg-bg_0 rounded-[100px] px-2"
                        onClick={() => refModalTokenList.current?.open()}
                    >
                        <span className="text-text_1 text-body3 mr-[2px] font-semibold">
                            {pairedToken?.token_base?.symbol?.toUpperCase()}
                        </span>
                        <IconDownFill className="text-text_1 text-[14px] relative -top-[1px]" />
                    </div>
                    <ModalUTokenList
                        ref={refModalTokenList}
                        chainId={tradeToken?.token_base?.chain_id}
                        contract={tradeToken?.token_base?.contract}
                        onSelect={(token) => {
                            setPairedToken(tradeToken, token);
                        }}
                    />
                    {/* 传递回调函数 */}
                </Fragment>
            </div>
            <div className="mb-[8px] flex justify-between items-center">
                <span className="text-direction1 text-text_3">
                    {t("usdt_in_network", {
                        token:
                            pairedToken?.token_base?.symbol?.toUpperCase() ??
                            "",
                    })}
                </span>
                <div className="flex items-center">
                    <div className="w-[14px] h-[14px] rounded-full overflow-hidden">
                        <img
                            src={pairedToken?.token_base?.chain_logo}
                            alt={pairedToken?.token_base?.chain_name}
                        />
                    </div>
                    <span className="text-direction1 text-text_1 ml-[4px]">
                        {pairedToken?.token_base?.chain_name}
                    </span>
                </div>
            </div>
            <div className="mb-[8px] flex justify-between items-center">
                <span className="text-direction1 text-text_3 flex-none">
                    {t("available")}
                </span>
                <div
                    className="flex items-center cursor-pointer flex-auto overflow-hidden pl-1"
                    onClick={handleRevicePayment}
                >
                    <div className="flex-auto overflow-hidden text-ellipsis text-right">
                        <span className="text-direction1 text-text_1 whitespace-nowrap">
                            {formatBalance(availableBalance ?? "0")}{" "}
                            {availableSymbol}
                        </span>
                    </div>
                    {isConnectedTo(tradeTokenChainId) && (
                        <IconCirclePlus className="text-text_brand ml-0.5 -mt-[1px] text-body2 flex-none" />
                    )}
                </div>
            </div>
            <button
                className={`w-full py-2 h-[44px] flex items-center justify-center text-body3 font-semibold rounded-[4px] text-white ${tradeActionType === ActionType.Buy ? "bg-button_buy" : "bg-button_sell"} ${isLoading ? "opacity-35" : "opacity-100"}`}
                onClick={handleClick}
                disabled={isLoading} // 禁用按钮在加载时
            >
                {!isConnectedTo(tradeTokenChainId) ? (
                    <div className="flex items-center">
                        {isConnecting && (
                            <IconLoading className="text-h5 mr-1" />
                        )}
                        <span>{t("connect_wallet")}</span>
                    </div>
                ) : loading || isLoading ? (
                    <div className="loading-rotate-1s w-[22px] h-[22px]">
                        <IconSwapLoading className="loading-rotate-1s text-[24px]" />
                    </div>
                ) : tradeActionType === ActionType.Buy ? (
                    isInsufficientBalance ? (
                        <div className="flex items-center">
                            <span>
                                {t("deposit")}{" "}
                                {pairedToken?.token_base?.symbol?.toUpperCase()}
                            </span>
                            <IconCirclePlus className="text-White ml-1 text-body2" />
                        </div>
                    ) : (
                        `${t("buy")} ${tradeToken?.token_base?.symbol?.toUpperCase()}`
                    )
                ) : (
                    `${t("sell")} ${tradeToken?.token_base?.symbol?.toUpperCase()}`
                )}
            </button>
        </div>
    );
};

export default memo(TradeBodySwap);
